import { useState, type MouseEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { Building2, LogOut, Loader2, Briefcase, AlertTriangle } from 'lucide-react';

/**
 * Account Settings: Shops & Memberships card.
 * Lists active shop memberships with "Leave Shop" action.
 */
export function AccountShopMemberships() {
  const { user, userShops, membership, isPassportMode, signOut } = useAuth();
  const [leavingShopId, setLeavingShopId] = useState<string | null>(null);
  const [isLeaving, setIsLeaving] = useState(false);

  // Fetch all active memberships for the user
  const { data: memberships, isLoading, refetch } = useQuery({
    queryKey: ['user-all-memberships', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from('memberships')
        .select('id, shop_id, role, status, created_at')
        .eq('user_id', user.id)
        .eq('status', 'active');
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id,
  });

  // Check owner count for each shop where user is OWNER
  const { data: ownerCounts } = useQuery({
    queryKey: ['owner-counts', user?.id, memberships?.map(m => m.shop_id)],
    queryFn: async () => {
      if (!memberships) return {};
      const ownerShopIds = memberships.filter(m => m.role === 'OWNER').map(m => m.shop_id);
      if (ownerShopIds.length === 0) return {};

      const counts: Record<string, number> = {};
      for (const shopId of ownerShopIds) {
        const { count } = await supabase
          .from('memberships')
          .select('id', { count: 'exact', head: true })
          .eq('shop_id', shopId)
          .eq('role', 'OWNER')
          .eq('status', 'active');
        counts[shopId] = count || 0;
      }
      return counts;
    },
    enabled: !!memberships && memberships.length > 0,
  });

  const handleLeaveShop = async (event?: MouseEvent<HTMLButtonElement>) => {
    event?.preventDefault();
    event?.stopPropagation();

    const shopIdToLeave = leavingShopId;
    if (!shopIdToLeave) return;
    setLeavingShopId(null);
    setIsLeaving(true);

    try {
      const { data, error } = await supabase.functions.invoke('leave-shop', {
        body: { shop_id: shopIdToLeave },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast({
        title: 'Left shop',
        description: 'You have successfully left the shop.',
      });

      refetch();
      // Reload the page to refresh auth context
      window.location.reload();
    } catch (err: any) {
      toast({
        title: 'Failed to leave shop',
        description: err.message || 'An error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsLeaving(false);
    }
  };

  const getShopName = (shopId: string) => {
    return userShops.find(s => s.id === shopId)?.name || 'Unknown Shop';
  };

  const isLastOwner = (shopId: string) => {
    return (ownerCounts?.[shopId] || 0) <= 1;
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Shop Memberships
          </CardTitle>
          <CardDescription>
            Shops you are currently a member of
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isPassportMode && (
            <div className="flex items-start gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20 mb-4">
              <Briefcase className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <p className="text-sm font-medium">Passport Mode</p>
                <p className="text-sm text-muted-foreground">
                  You are not currently attached to any shop. Your training records and tools remain accessible.
                </p>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground py-4">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading memberships...
            </div>
          ) : memberships && memberships.length > 0 ? (
            <div className="space-y-3">
              {memberships.map((m) => {
                const isOwner = m.role === 'OWNER';
                const lastOwner = isOwner && isLastOwner(m.shop_id);

                return (
                  <div
                    key={m.id}
                    className="flex items-center justify-between p-3 rounded-lg border"
                  >
                    <div className="flex items-center gap-3">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="font-medium text-sm">{getShopName(m.shop_id)}</p>
                        <Badge variant="secondary" className="mt-1 text-xs">
                          {m.role}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {lastOwner && (
                        <span className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          Last owner
                        </span>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setLeavingShopId(m.shop_id)}
                        disabled={lastOwner}
                        className="text-destructive hover:text-destructive"
                      >
                        <LogOut className="h-4 w-4 mr-1" />
                        Leave
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-2">No active memberships</p>
          )}
        </CardContent>
      </Card>

      {/* Leave Shop Confirmation */}
      <AlertDialog open={!!leavingShopId} onOpenChange={(open) => !open && setLeavingShopId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Leave Shop</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to leave <strong>{leavingShopId ? getShopName(leavingShopId) : ''}</strong>?
              You will lose access to this shop's data. A shop manager can re-invite you later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLeaving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              type="button"
              onClick={handleLeaveShop}
              disabled={isLeaving}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isLeaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Leave Shop
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
